/**
 * Language list — 30+ ngôn ngữ với flag emoji + search support.
 *
 * Dùng trong:
 * - OnboardingTour WelcomeModal (step 2 + 3 lang picker)
 * - ProjectDetail tab Cấu hình (source/target lang dropdowns)
 * - CreateProjectModal (defer — hiện dùng default từ user.preferred_*_lang)
 */

export interface Language {
  /** ISO 639-1 2-letter code (vd 'vi', 'en') */
  code: string;
  /** Native name, hiển thị chính (vd "Tiếng Việt", "日本語") */
  name: string;
  /** English name, search fallback (vd "Vietnamese") */
  nameEn: string;
  /** Unicode flag emoji — render native browser */
  flag: string;
  /** ISO 3166-1 alpha-2 lowercase — cho Twemoji/Flagpedia SVG fallback */
  countryCode: string;
  /** Top 8 popular — hiện đầu khi user chưa search */
  popular?: boolean;
}

export const LANGUAGES: Language[] = [
  // ── Popular (top 8, hiện đầu khi chưa search) ──
  { code: 'vi', name: 'Tiếng Việt',          nameEn: 'Vietnamese',  flag: '🇻🇳', countryCode: 'vn', popular: true },
  { code: 'en', name: 'English',             nameEn: 'English',     flag: '🇬🇧', countryCode: 'gb', popular: true },
  { code: 'ja', name: '日本語',                nameEn: 'Japanese',    flag: '🇯🇵', countryCode: 'jp', popular: true },
  { code: 'ko', name: '한국어',                nameEn: 'Korean',      flag: '🇰🇷', countryCode: 'kr', popular: true },
  { code: 'zh', name: '中文 (简体)',           nameEn: 'Chinese (Simplified)', flag: '🇨🇳', countryCode: 'cn', popular: true },
  { code: 'fr', name: 'Français',            nameEn: 'French',      flag: '🇫🇷', countryCode: 'fr', popular: true },
  { code: 'de', name: 'Deutsch',             nameEn: 'German',      flag: '🇩🇪', countryCode: 'de', popular: true },
  { code: 'th', name: 'ภาษาไทย',             nameEn: 'Thai',        flag: '🇹🇭', countryCode: 'th', popular: true },

  // ── Chinese variants ──
  { code: 'zh-TW', name: '中文 (繁體)',         nameEn: 'Chinese (Traditional)', flag: '🇹🇼', countryCode: 'tw' },
  { code: 'yue', name: '廣東話',                nameEn: 'Cantonese',   flag: '🇭🇰', countryCode: 'hk' },

  // ── European (Romance) ──
  { code: 'es', name: 'Español',             nameEn: 'Spanish',     flag: '🇪🇸', countryCode: 'es' },
  { code: 'pt', name: 'Português',           nameEn: 'Portuguese',  flag: '🇵🇹', countryCode: 'pt' },
  { code: 'pt-BR', name: 'Português (Brasil)', nameEn: 'Portuguese (Brazilian)', flag: '🇧🇷', countryCode: 'br' },
  { code: 'it', name: 'Italiano',            nameEn: 'Italian',     flag: '🇮🇹', countryCode: 'it' },
  { code: 'ro', name: 'Română',              nameEn: 'Romanian',    flag: '🇷🇴', countryCode: 'ro' },
  { code: 'ca', name: 'Català',              nameEn: 'Catalan',     flag: '🇪🇸', countryCode: 'es' },
  { code: 'eu', name: 'Euskara',             nameEn: 'Basque',      flag: '🇪🇸', countryCode: 'es' },
  { code: 'gl', name: 'Galego',              nameEn: 'Galician',    flag: '🇪🇸', countryCode: 'es' },
  { code: 'la', name: 'Latina',              nameEn: 'Latin',       flag: '🇻🇦', countryCode: 'va' },

  // ── European (Germanic) ──
  { code: 'nl', name: 'Nederlands',          nameEn: 'Dutch',       flag: '🇳🇱', countryCode: 'nl' },
  { code: 'sv', name: 'Svenska',             nameEn: 'Swedish',     flag: '🇸🇪', countryCode: 'se' },
  { code: 'da', name: 'Dansk',               nameEn: 'Danish',      flag: '🇩🇰', countryCode: 'dk' },
  { code: 'no', name: 'Norsk',               nameEn: 'Norwegian',   flag: '🇳🇴', countryCode: 'no' },
  { code: 'is', name: 'Íslenska',            nameEn: 'Icelandic',   flag: '🇮🇸', countryCode: 'is' },
  { code: 'af', name: 'Afrikaans',           nameEn: 'Afrikaans',   flag: '🇿🇦', countryCode: 'za' },
  { code: 'lb', name: 'Lëtzebuergesch',      nameEn: 'Luxembourgish', flag: '🇱🇺', countryCode: 'lu' },
  { code: 'fy', name: 'Frysk',               nameEn: 'Frisian',     flag: '🇳🇱', countryCode: 'nl' },

  // ── European (Slavic) ──
  { code: 'ru', name: 'Русский',             nameEn: 'Russian',     flag: '🇷🇺', countryCode: 'ru' },
  { code: 'pl', name: 'Polski',              nameEn: 'Polish',      flag: '🇵🇱', countryCode: 'pl' },
  { code: 'uk', name: 'Українська',          nameEn: 'Ukrainian',   flag: '🇺🇦', countryCode: 'ua' },
  { code: 'cs', name: 'Čeština',             nameEn: 'Czech',       flag: '🇨🇿', countryCode: 'cz' },
  { code: 'sk', name: 'Slovenčina',          nameEn: 'Slovak',      flag: '🇸🇰', countryCode: 'sk' },
  { code: 'bg', name: 'Български',           nameEn: 'Bulgarian',   flag: '🇧🇬', countryCode: 'bg' },
  { code: 'hr', name: 'Hrvatski',            nameEn: 'Croatian',    flag: '🇭🇷', countryCode: 'hr' },
  { code: 'sr', name: 'Српски',              nameEn: 'Serbian',     flag: '🇷🇸', countryCode: 'rs' },
  { code: 'bs', name: 'Bosanski',            nameEn: 'Bosnian',     flag: '🇧🇦', countryCode: 'ba' },
  { code: 'sl', name: 'Slovenščina',         nameEn: 'Slovenian',   flag: '🇸🇮', countryCode: 'si' },
  { code: 'mk', name: 'Македонски',          nameEn: 'Macedonian',  flag: '🇲🇰', countryCode: 'mk' },
  { code: 'be', name: 'Беларуская',          nameEn: 'Belarusian',  flag: '🇧🇾', countryCode: 'by' },

  // ── European (Baltic) ──
  { code: 'lt', name: 'Lietuvių',            nameEn: 'Lithuanian',  flag: '🇱🇹', countryCode: 'lt' },
  { code: 'lv', name: 'Latviešu',            nameEn: 'Latvian',     flag: '🇱🇻', countryCode: 'lv' },
  { code: 'et', name: 'Eesti',               nameEn: 'Estonian',    flag: '🇪🇪', countryCode: 'ee' },

  // ── European (Other) ──
  { code: 'fi', name: 'Suomi',               nameEn: 'Finnish',     flag: '🇫🇮', countryCode: 'fi' },
  { code: 'hu', name: 'Magyar',              nameEn: 'Hungarian',   flag: '🇭🇺', countryCode: 'hu' },
  { code: 'el', name: 'Ελληνικά',            nameEn: 'Greek',       flag: '🇬🇷', countryCode: 'gr' },
  { code: 'sq', name: 'Shqip',               nameEn: 'Albanian',    flag: '🇦🇱', countryCode: 'al' },
  { code: 'mt', name: 'Malti',               nameEn: 'Maltese',     flag: '🇲🇹', countryCode: 'mt' },
  { code: 'ga', name: 'Gaeilge',             nameEn: 'Irish',       flag: '🇮🇪', countryCode: 'ie' },
  { code: 'cy', name: 'Cymraeg',             nameEn: 'Welsh',       flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', countryCode: 'gb-wls' },
  { code: 'gd', name: 'Gàidhlig',            nameEn: 'Scottish Gaelic', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', countryCode: 'gb-sct' },

  // ── Middle Eastern ──
  { code: 'ar', name: 'العربية',             nameEn: 'Arabic',      flag: '🇸🇦', countryCode: 'sa' },
  { code: 'he', name: 'עברית',               nameEn: 'Hebrew',      flag: '🇮🇱', countryCode: 'il' },
  { code: 'tr', name: 'Türkçe',              nameEn: 'Turkish',     flag: '🇹🇷', countryCode: 'tr' },
  { code: 'fa', name: 'فارسی',               nameEn: 'Persian',     flag: '🇮🇷', countryCode: 'ir' },
  { code: 'ku', name: 'Kurdî',               nameEn: 'Kurdish',     flag: '🇮🇶', countryCode: 'iq' },
  { code: 'az', name: 'Azərbaycan',          nameEn: 'Azerbaijani', flag: '🇦🇿', countryCode: 'az' },
  { code: 'hy', name: 'Հայերեն',             nameEn: 'Armenian',    flag: '🇦🇲', countryCode: 'am' },
  { code: 'ka', name: 'ქართული',             nameEn: 'Georgian',    flag: '🇬🇪', countryCode: 'ge' },

  // ── Central Asian ──
  { code: 'uz', name: 'Oʻzbek',              nameEn: 'Uzbek',       flag: '🇺🇿', countryCode: 'uz' },
  { code: 'kk', name: 'Қазақ',               nameEn: 'Kazakh',      flag: '🇰🇿', countryCode: 'kz' },
  { code: 'ky', name: 'Кыргызча',            nameEn: 'Kyrgyz',      flag: '🇰🇬', countryCode: 'kg' },
  { code: 'tg', name: 'Тоҷикӣ',              nameEn: 'Tajik',       flag: '🇹🇯', countryCode: 'tj' },
  { code: 'tk', name: 'Türkmen',             nameEn: 'Turkmen',     flag: '🇹🇲', countryCode: 'tm' },
  { code: 'mn', name: 'Монгол',              nameEn: 'Mongolian',   flag: '🇲🇳', countryCode: 'mn' },

  // ── South Asian ──
  { code: 'hi', name: 'हिन्दी',               nameEn: 'Hindi',       flag: '🇮🇳', countryCode: 'in' },
  { code: 'bn', name: 'বাংলা',               nameEn: 'Bengali',     flag: '🇧🇩', countryCode: 'bd' },
  { code: 'ur', name: 'اردو',                nameEn: 'Urdu',        flag: '🇵🇰', countryCode: 'pk' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ',                nameEn: 'Punjabi',     flag: '🇮🇳', countryCode: 'in' },
  { code: 'ta', name: 'தமிழ்',              nameEn: 'Tamil',       flag: '🇱🇰', countryCode: 'lk' },
  { code: 'te', name: 'తెలుగు',              nameEn: 'Telugu',      flag: '🇮🇳', countryCode: 'in' },
  { code: 'mr', name: 'मराठी',                nameEn: 'Marathi',     flag: '🇮🇳', countryCode: 'in' },
  { code: 'gu', name: 'ગુજરાતી',              nameEn: 'Gujarati',    flag: '🇮🇳', countryCode: 'in' },
  { code: 'kn', name: 'ಕನ್ನಡ',                 nameEn: 'Kannada',     flag: '🇮🇳', countryCode: 'in' },
  { code: 'ml', name: 'മലയാളം',                nameEn: 'Malayalam',   flag: '🇮🇳', countryCode: 'in' },
  { code: 'or', name: 'ଓଡ଼ିଆ',                nameEn: 'Odia',        flag: '🇮🇳', countryCode: 'in' },
  { code: 'as', name: 'অসমীয়া',              nameEn: 'Assamese',    flag: '🇮🇳', countryCode: 'in' },
  { code: 'si', name: 'සිංහල',                nameEn: 'Sinhala',     flag: '🇱🇰', countryCode: 'lk' },
  { code: 'ne', name: 'नेपाली',               nameEn: 'Nepali',      flag: '🇳🇵', countryCode: 'np' },
  { code: 'dv', name: 'ދިވެހި',              nameEn: 'Dhivehi',     flag: '🇲🇻', countryCode: 'mv' },

  // ── Southeast Asian ──
  { code: 'id', name: 'Bahasa Indonesia',    nameEn: 'Indonesian',  flag: '🇮🇩', countryCode: 'id' },
  { code: 'ms', name: 'Bahasa Melayu',       nameEn: 'Malay',       flag: '🇲🇾', countryCode: 'my' },
  { code: 'tl', name: 'Filipino',            nameEn: 'Filipino',    flag: '🇵🇭', countryCode: 'ph' },
  { code: 'my', name: 'မြန်မာဘာသာ',          nameEn: 'Burmese',     flag: '🇲🇲', countryCode: 'mm' },
  { code: 'km', name: 'ខ្មែរ',               nameEn: 'Khmer',       flag: '🇰🇭', countryCode: 'kh' },
  { code: 'lo', name: 'ລາວ',                 nameEn: 'Lao',         flag: '🇱🇦', countryCode: 'la' },
  { code: 'jv', name: 'Basa Jawa',           nameEn: 'Javanese',    flag: '🇮🇩', countryCode: 'id' },
  { code: 'su', name: 'Basa Sunda',          nameEn: 'Sundanese',   flag: '🇮🇩', countryCode: 'id' },

  // ── African ──
  { code: 'sw', name: 'Kiswahili',           nameEn: 'Swahili',     flag: '🇰🇪', countryCode: 'ke' },
  { code: 'am', name: 'አማርኛ',                nameEn: 'Amharic',     flag: '🇪🇹', countryCode: 'et' },
  { code: 'ha', name: 'Hausa',               nameEn: 'Hausa',       flag: '🇳🇬', countryCode: 'ng' },
  { code: 'yo', name: 'Yorùbá',              nameEn: 'Yoruba',      flag: '🇳🇬', countryCode: 'ng' },
  { code: 'ig', name: 'Igbo',                nameEn: 'Igbo',        flag: '🇳🇬', countryCode: 'ng' },
  { code: 'zu', name: 'isiZulu',             nameEn: 'Zulu',        flag: '🇿🇦', countryCode: 'za' },
  { code: 'xh', name: 'isiXhosa',            nameEn: 'Xhosa',       flag: '🇿🇦', countryCode: 'za' },
  { code: 'sn', name: 'chiShona',            nameEn: 'Shona',       flag: '🇿🇼', countryCode: 'zw' },
  { code: 'st', name: 'Sesotho',             nameEn: 'Sesotho',     flag: '🇱🇸', countryCode: 'ls' },
  { code: 'rw', name: 'Kinyarwanda',         nameEn: 'Kinyarwanda', flag: '🇷🇼', countryCode: 'rw' },
  { code: 'so', name: 'Soomaali',            nameEn: 'Somali',      flag: '🇸🇴', countryCode: 'so' },
  { code: 'mg', name: 'Malagasy',            nameEn: 'Malagasy',    flag: '🇲🇬', countryCode: 'mg' },
  { code: 'ny', name: 'Chichewa',            nameEn: 'Chichewa',    flag: '🇲🇼', countryCode: 'mw' },

  // ── American ──
  { code: 'ht', name: 'Kreyòl Ayisyen',      nameEn: 'Haitian Creole', flag: '🇭🇹', countryCode: 'ht' },
  { code: 'qu', name: 'Runa Simi',           nameEn: 'Quechua',     flag: '🇵🇪', countryCode: 'pe' },
  { code: 'gn', name: 'Avañeʼẽ',             nameEn: 'Guarani',     flag: '🇵🇾', countryCode: 'py' },

  // ── Pacific ──
  { code: 'mi', name: 'Te Reo Māori',        nameEn: 'Maori',       flag: '🇳🇿', countryCode: 'nz' },
  { code: 'sm', name: 'Gagana Sāmoa',        nameEn: 'Samoan',      flag: '🇼🇸', countryCode: 'ws' },
  { code: 'haw', name: 'ʻŌlelo Hawaiʻi',     nameEn: 'Hawaiian',    flag: '🇺🇸', countryCode: 'us' },

  // ── Constructed ──
  { code: 'eo', name: 'Esperanto',           nameEn: 'Esperanto',   flag: '🏳️', countryCode: 'eo' },
];

/**
 * Search ngôn ngữ theo query (case-insensitive, match name/nameEn/code).
 * Empty query → return top 8 popular.
 */
export function searchLanguages(query: string): Language[] {
  const q = query.toLowerCase().trim();
  if (!q) return LANGUAGES.filter(l => l.popular);
  return LANGUAGES.filter(l =>
    l.name.toLowerCase().includes(q) ||
    l.nameEn.toLowerCase().includes(q) ||
    l.code.includes(q) ||
    l.countryCode.includes(q)
  );
}

/** Lookup by code. Return undefined nếu không tìm thấy. */
export function getLanguage(code: string): Language | undefined {
  return LANGUAGES.find(l => l.code === code);
}

/** Tên tiếng Việt cho các ngôn ngữ phổ biến — fallback về native name nếu không có. */
const LANGUAGE_NAMES_VI: Record<string, string> = {
  vi: 'Tiếng Việt',
  en: 'Tiếng Anh',
  ja: 'Tiếng Nhật',
  ko: 'Tiếng Hàn',
  zh: 'Tiếng Trung (Giản thể)',
  'zh-TW': 'Tiếng Trung (Phồn thể)',
  yue: 'Tiếng Quảng Đông',
  fr: 'Tiếng Pháp',
  de: 'Tiếng Đức',
  th: 'Tiếng Thái',
  es: 'Tiếng Tây Ban Nha',
  pt: 'Tiếng Bồ Đào Nha',
  'pt-BR': 'Tiếng Bồ Đào Nha (Brazil)',
  it: 'Tiếng Ý',
  ru: 'Tiếng Nga',
  pl: 'Tiếng Ba Lan',
  uk: 'Tiếng Ukraina',
  cs: 'Tiếng Séc',
  nl: 'Tiếng Hà Lan',
  sv: 'Tiếng Thụy Điển',
  da: 'Tiếng Đan Mạch',
  no: 'Tiếng Na Uy',
  fi: 'Tiếng Phần Lan',
  hu: 'Tiếng Hungary',
  el: 'Tiếng Hy Lạp',
  tr: 'Tiếng Thổ Nhĩ Kỳ',
  ar: 'Tiếng Ả Rập',
  he: 'Tiếng Do Thái',
  fa: 'Tiếng Ba Tư',
  hi: 'Tiếng Hindi',
  bn: 'Tiếng Bengal',
  ur: 'Tiếng Urdu',
  ta: 'Tiếng Tamil',
  id: 'Tiếng Indonesia',
  ms: 'Tiếng Mã Lai',
  tl: 'Tiếng Filipino',
  my: 'Tiếng Miến Điện',
  km: 'Tiếng Khmer',
  lo: 'Tiếng Lào',
  sw: 'Tiếng Swahili',
  ro: 'Tiếng Romania',
  bg: 'Tiếng Bulgaria',
  hr: 'Tiếng Croatia',
  sr: 'Tiếng Serbia',
  sk: 'Tiếng Slovakia',
  sl: 'Tiếng Slovenia',
  lt: 'Tiếng Litva',
  lv: 'Tiếng Latvia',
  et: 'Tiếng Estonia',
  ca: 'Tiếng Catalan',
  la: 'Tiếng Latinh',
  is: 'Tiếng Iceland',
  ga: 'Tiếng Ireland',
  cy: 'Tiếng Wales',
  mn: 'Tiếng Mông Cổ',
  ne: 'Tiếng Nepal',
  hy: 'Tiếng Armenia',
  ka: 'Tiếng Gruzia',
  az: 'Tiếng Azerbaijan',
  kk: 'Tiếng Kazakh',
  uz: 'Tiếng Uzbek',
  am: 'Tiếng Amhara',
  af: 'Tiếng Afrikaans',
  zu: 'Tiếng Zulu',
  eo: 'Tiếng Esperanto',
};

/** Tên ngôn ngữ ưu tiên tiếng Việt — fallback về native name → code. */
export function getLanguageNameVi(code: string): string {
  if (LANGUAGE_NAMES_VI[code]) return LANGUAGE_NAMES_VI[code];
  return getLanguage(code)?.name ?? code.toUpperCase();
}

/** Default pair VN workflow */
export const DEFAULT_SOURCE_LANG = 'vi';
export const DEFAULT_TARGET_LANG = 'en';
